import React from 'react';
import { X } from 'lucide-react';
import type { Site, EditFormData } from './types';
import { formatDate } from './utils';
import { getPublishedDomain } from '../../utils/publishedUrl';

interface EditSiteModalProps {
  site: Site;
  formData: EditFormData;
  onFormDataChange: (data: EditFormData) => void;
  onSave: () => void;
  onCancel: () => void;
  onToggleLandingPage: (site: Site, isLandingPage: boolean) => void;
  isSubmitting: boolean;
}

export const EditSiteModal: React.FC<EditSiteModalProps> = ({
  site,
  formData,
  onFormDataChange,
  onSave,
  onCancel,
  onToggleLandingPage,
  isSubmitting,
}) => (
  <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
    <div className="bg-white rounded-lg p-6 max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
      <div className="flex justify-between items-start mb-6">
        <div>
          <h3 className="text-lg font-semibold text-gray-900">Edit Site</h3>
          <p className="text-sm text-gray-500 mt-1">
            Update site name, subdomain, or custom domain
          </p>
        </div>
        <button
          onClick={onCancel}
          disabled={isSubmitting}
          className="text-gray-400 hover:text-gray-600"
        >
          <X size={24} />
        </button>
      </div>

      <div className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Site Name</label>
          <input
            type="text"
            value={formData.name}
            onChange={e => onFormDataChange({ ...formData, name: e.target.value })}
            className="input-field w-full"
            placeholder="My Website"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Subdomain</label>
          <div className="flex items-center space-x-2">
            <input
              type="text"
              value={formData.subdomain}
              onChange={e =>
                onFormDataChange({
                  ...formData,
                  subdomain: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ''),
                })
              }
              className="input-field flex-1"
              placeholder="mysite"
            />
            <span className="text-gray-500">.{getPublishedDomain()}</span>
          </div>
          <p className="text-xs text-gray-500 mt-1">
            Only lowercase letters, numbers, and hyphens allowed
          </p>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Custom Domain (optional)
          </label>
          <input
            type="text"
            value={formData.customDomain}
            onChange={e => onFormDataChange({ ...formData, customDomain: e.target.value })}
            className="input-field w-full"
            placeholder="example.com"
          />
          <p className="text-xs text-gray-500 mt-1">Leave empty to remove custom domain</p>
        </div>

        <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
          <label className="flex items-start gap-3 cursor-pointer">
            <input
              type="checkbox"
              checked={site.isLandingPage}
              disabled={isSubmitting || (!site.isLandingPage && !site.isPublished)}
              onChange={e => onToggleLandingPage(site, e.target.checked)}
              className="mt-1 h-4 w-4 text-purple-600 border-gray-300 rounded disabled:opacity-50"
            />
            <div className="flex-1">
              <div className="text-sm font-medium text-purple-900">
                Use as Compete Pages landing page
              </div>
              <p className="text-xs text-purple-800 mt-1">
                When enabled, this site replaces the static landing page at{' '}
                <code>{getPublishedDomain()}</code> and its sub-pages. Any previously designated
                site will be un-designated automatically.
              </p>
              {!site.isPublished && !site.isLandingPage && (
                <p className="text-xs text-amber-700 mt-1">
                  Publish this site before you can designate it as the landing page.
                </p>
              )}
            </div>
          </label>
        </div>

        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <p className="text-sm text-blue-800">
            <strong>Owner:</strong> {site.userName} ({site.userEmail})
          </p>
          <p className="text-sm text-blue-800 mt-1">
            <strong>Created:</strong> {formatDate(site.createdAt)}
          </p>
        </div>
      </div>

      <div className="flex space-x-4 mt-6">
        <button
          onClick={onSave}
          disabled={isSubmitting || !formData.name || !formData.subdomain}
          className="btn-primary flex-1"
        >
          {isSubmitting ? 'Saving...' : 'Save Changes'}
        </button>
        <button onClick={onCancel} disabled={isSubmitting} className="btn-secondary flex-1">
          Cancel
        </button>
      </div>
    </div>
  </div>
);
